import { addConsumptionTax } from "./tax";

/**
 * `GET /api/me` のレスポンスのうち残高算出に使うフィールド。
 * - `balance`（新バケット）: **税抜**
 * - `available_balance`（旧）: **税込**
 *
 * `committed_incoming` は親オファーを受諾して得た「進行中(受信)」残高で、
 * 再委託オファーの ``escrow_lock`` 原資として利用可能（BE
 * ``payments/escrow_service.py`` の Phase 1.5 拡張）。
 *
 * `committed_outgoing` は自分が委託したオファーで拘束中の残高。``balance_free``
 * から既に控除されているため、利用可能額の計算には登場しないが、進行中モーダルの
 * サマリ表示で「拘束前の入金額（``free + committed_outgoing``）」を復元するため
 * に参照する。
 */
export interface MeBalanceSource {
  balance?: {
    free?: string | number | null;
    settled?: string | number | null;
    committed_incoming?: string | number | null;
    committed_outgoing?: string | number | null;
  } | null;
  available_balance?: string | number | null;
}

export interface DisplayBalances {
  /** 残高（税込・新旧合算）: 旧 `available_balance` + `(free + settled) × 1.1` */
  total: number;
  /** 振込申請可能額（税込）: 新仕様 `settled × 1.1`。payment-requests API は settled（税抜）を上限に申請する。 */
  withdrawable: number;
  /** 新仕様の振込原資 `settled`（税抜）。payment-requests API の申請可否判定に使う。 */
  settledExclusive: number;
}

/** オファー送信時の escrow 原資チェック結果。 */
export interface OfferEscrowBalanceCheck {
  /** 対価が 1 円以上で残高チェックが意味を持つとき true。対価 0 / 未入力なら false。 */
  applicable: boolean;
  /** 残高が足りているか。`applicable=false` のときは常に true（チェック対象外）。 */
  sufficient: boolean;
  /** 契約金額（税抜・整数円）。対価行の合計（`computeCompensationSubtotalExclusive`）と同値。 */
  contractAmountExclusive: number | null;
  /**
   * 実質利用可能額（税抜）= `balance.free + balance.settled + balance.committed_incoming`
   * − 他の未送信ドラフトが予約している対価合計。
   * 送信済み（承認待ち・進行中）のオファーは BE の ``escrow_lock`` で
   * 既に `free` / `settled` / `committed_incoming` から `committed_outgoing`
   * へ移動済みのため、ここでは二重に差し引かない。
   * `committed_incoming` は親オファーを受諾して得た予算であり、再委託
   * （子オファー）の原資として利用できる（BE ``escrow_lock`` Phase 1.5）。
   */
  availableExclusive: number;
  /** 他の未送信ドラフトが予約している対価合計（税抜）。 */
  reservedByOtherDraftsExclusive: number;
  /** 不足額（税抜）。十分なとき 0。 */
  shortfallExclusive: number;
}

const toNumber = (v: string | number | null | undefined): number => {
  const n = Number(v ?? 0);
  return Number.isFinite(n) ? n : 0;
};

/**
 * 新旧互換の税込合算残高を算出する。
 *
 * 旧フィールド（税込）はそのまま、新バケット（税抜）は税込変換してから合算する。
 * 旧仕様プロジェクトが廃止されれば旧フィールドは 0 になり、新仕様のみで成立する。
 */
export const computeDisplayBalances = (
  userData: MeBalanceSource | null | undefined,
): DisplayBalances => {
  const balance = userData?.balance ?? {};
  const free = toNumber(balance?.free);
  const settled = toNumber(balance?.settled);
  const legacyAvailable = toNumber(userData?.available_balance);

  return {
    total: legacyAvailable + addConsumptionTax(free + settled),
    withdrawable: addConsumptionTax(settled),
    settledExclusive: settled,
  };
};

/**
 * 新仕様オファー送信時に ``escrow_lock`` が参照する原資（税抜）を返す。
 * BE ``payments.escrow_service.escrow_lock`` と同じ
 * ``free + settled + committed_incoming``。
 *
 * `committed_incoming` は親オファーを受諾して得た予算（進行中受信）であり、
 * Phase 1.5 で再委託（子オファー）の原資として利用可能になった。
 */
export const computeOfferEscrowAvailableExclusive = (
  userData: MeBalanceSource | null | undefined,
): number => {
  const balance = userData?.balance ?? {};
  return (
    toNumber(balance?.free) +
    toNumber(balance?.settled) +
    toNumber(balance?.committed_incoming)
  );
};

/**
 * オファー作成フォームの対価と利用可能残高（新仕様 escrow 原資）を比較する。
 *
 * - 対価未入力 / 0 円のときは対象外（escrow 発火しないため）。
 * - 比較単位は BE と揃えて**税抜・整数円**（``contract_amount`` vs ``free + settled``）。
 * - `reservedByOtherDraftsExclusive`: まだ送信していない他のオファードラフトが
 *   予約している対価合計。これを差し引くことで、複数ドラフトを並行作成しても
 *   合計が残高を超えないようにする（送信済み分は ``escrow_lock`` 済みなので含めない）。
 */
export const evaluateOfferEscrowBalanceCheck = ({
  contractAmountExclusive,
  userData,
  reservedByOtherDraftsExclusive = 0,
}: {
  contractAmountExclusive: number | null;
  userData: MeBalanceSource | null | undefined;
  reservedByOtherDraftsExclusive?: number;
}): OfferEscrowBalanceCheck => {
  const reserved = Math.max(0, reservedByOtherDraftsExclusive);
  const availableExclusive = Math.max(
    0,
    computeOfferEscrowAvailableExclusive(userData) - reserved,
  );

  const amount =
    contractAmountExclusive === null ||
    !Number.isFinite(contractAmountExclusive)
      ? 0
      : contractAmountExclusive;

  if (amount <= 0) {
    return {
      applicable: false,
      sufficient: true,
      contractAmountExclusive,
      availableExclusive,
      reservedByOtherDraftsExclusive: reserved,
      shortfallExclusive: 0,
    };
  }

  const sufficient = availableExclusive >= amount;
  return {
    applicable: true,
    sufficient,
    contractAmountExclusive: amount,
    availableExclusive,
    reservedByOtherDraftsExclusive: reserved,
    shortfallExclusive: sufficient ? 0 : amount - availableExclusive,
  };
};
